import type { FC } from "react"
import React, { useMemo } from "react"
import { Flex, Stack, Text, useDisclosure } from "@chakra-ui/react"
import { BigNumber } from "ethers"

import {
  VotesSortBy,
  DelegatesSortBy,
  type OnchainVote,
  type Delegate,
  useProposalNotYetVotedQuery,
} from "query/graphql"
import CardContainer from "common/components/CardContainer"
import UserIdentity from "common/components/columns/UserIdentity"
import { getWeightLabel } from "common/helpers/bignumber"
import { useOrganization } from "organization/providers/OrganizationProvider"
import EmptyEntity from "common/components/EmptyEntity"
import LoadMoreButton from "common/components/LoadMoreButton"
import { pluralize } from "common/helpers/string"

const DISPLAYED_DELEGATES_LIMIT = 5

type Props = {
  proposalId: string
}

const ProposalNotYetVoted: FC<Props> = ({ proposalId }) => {
  const { organization } = useOrganization()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { data, isLoading } = useProposalNotYetVotedQuery({
    input: {
      id: proposalId,
    },
    delegatesInput: {
      filters: {
        organizationId: organization?.id,
      },
      sort: {
        sortBy: DelegatesSortBy.Votes,
        isDescending: true,
      },
      page: {
        limit: 20,
      },
    },
    votesInput: {
      filters: {
        proposalId,
        includePendingVotes: true,
      },
      sort: {
        sortBy: VotesSortBy.Amount,
        isDescending: true,
      },
      page: {
        limit: 20,
      },
    },
  })

  const votes = data?.votes.nodes.filter(
    (vote) => "chainId" in vote,
  ) as OnchainVote[]

  const delegates = data?.delegates?.nodes as Delegate[]

  const notYetVotedDelegates = useMemo(() => {
    if (!votes || !delegates) return []

    // Create a Set of addresses that have already voted
    const votedAddresses = new Set(
      votes.map((vote) => vote.voter.address.toLowerCase()),
    )

    // Filter delegates who haven't voted and have voting power
    return delegates.filter(
      (delegate) =>
        !votedAddresses.has(delegate.account.address.toLowerCase()) &&
        Number(delegate.votesCount) > 0,
    )
  }, [delegates, votes])

  const totalVotingPower = useMemo(() => {
    return notYetVotedDelegates.reduce(
      (acc, delegate) => BigNumber.from(delegate.votesCount).add(acc),
      BigNumber.from(0),
    )
  }, [notYetVotedDelegates])

  if (isLoading) {
    return (
      <CardContainer>
        <Stack spacing={4}>
          <Text>Loading...</Text>
        </Stack>
      </CardContainer>
    )
  }

  if (!notYetVotedDelegates.length) {
    return (
      <CardContainer>
        <Stack spacing={4}>
          <EmptyEntity
            description="All top delegates have voted on this proposal"
            title="No remaining voters"
          />
        </Stack>
      </CardContainer>
    )
  }

  const displayDelegates = isOpen
    ? notYetVotedDelegates
    : notYetVotedDelegates.slice(0, DISPLAYED_DELEGATES_LIMIT)

  return (
    <Stack spacing={0}>
      <Flex
        borderBottom="1px solid"
        borderColor="gray.100"
        justify="space-between"
        p={4}
      >
        <Text color="gray.600" fontSize="sm">
          Top {notYetVotedDelegates.length}{" "}
          {pluralize(notYetVotedDelegates.length, "address", "addresses")}
        </Text>
        <Text color="gray.600" fontSize="sm">
          Total uncast votes:{" "}
          {getWeightLabel(
            totalVotingPower.toString(),
            data?.proposal?.governor?.token?.decimals ?? 18,
          )}{" "}
        </Text>
      </Flex>

      <Stack spacing={0}>
        {displayDelegates.map((delegate) => (
          <Flex
            key={delegate.id}
            _hover={{ bg: "gray.50" }}
            align="center"
            borderBottom="1px solid"
            borderColor="gray.100"
            justify="space-between"
            px={4}
            py={3}
          >
            <UserIdentity
              hasCopyButton
              account={delegate.account}
              slug={organization?.slug}
            />
            <Text fontWeight="medium">
              {getWeightLabel(
                delegate.votesCount,
                data?.proposal?.governor?.token?.decimals ?? 18,
              )}
            </Text>
          </Flex>
        ))}
      </Stack>

      {notYetVotedDelegates.length > DISPLAYED_DELEGATES_LIMIT ? (
        <Flex justify="center" p={4}>
          <LoadMoreButton
            flex={1}
            isDisabled={false}
            label={isOpen ? "Show less" : "Show more"}
            variant="tertiary"
            onClick={isOpen ? onClose : onOpen}
          />
        </Flex>
      ) : null}
    </Stack>
  )
}

export default ProposalNotYetVoted
